/**
 * Keeps the live socket open for the whole signed-in shell, and turns its frames into
 * query invalidations. Mounted once, in `AppShell`.
 *
 * Nothing here knows about any one screen: a frame names the query keys it makes stale
 * (`keysForFrame`), and whichever screen is showing those keys refetches.
 */
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAuthStore } from "@/store/auth";

import { refreshAccessToken } from "@/lib/api";
import {
  ALL_INBOX_KEYS,
  RealtimeClient,
  keysForFrame,
  realtimeUrl,
  type QueryKeyPrefix,
  type RealtimeFrame,
  type RealtimeStatus,
  type SocketLike,
} from "@/lib/realtime";
import { usePresence } from "@/features/inbox/presence";
import { useRealtimeStatus } from "@/lib/realtime-status";
import { readAccessToken } from "@/lib/tokens";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

function invalidateAll(queryClient: QueryClient, keys: readonly QueryKeyPrefix[]): void {
  for (const prefix of keys) {
    void queryClient.invalidateQueries({ queryKey: prefix });
  }
}

function createSocket(url: string, protocols: readonly string[]): SocketLike {
  return new WebSocket(url, [...protocols]);
}

async function freshToken(): Promise<string | null> {
  return (await refreshAccessToken()) ? readAccessToken() : null;
}

function publishStatus(status: RealtimeStatus): void {
  useRealtimeStatus.getState().setStatus(status);
}

export function useRealtimeSync(): void {
  const queryClient = useQueryClient();
  // The inbox socket is an agency's. Without an agency the server refuses the handshake.
  const hasAgency = useAuthStore((state) => Boolean(state.user?.tenant));

  useEffect(() => {
    if (!hasAgency) {
      return;
    }
    const client = new RealtimeClient({
      url: realtimeUrl(API_BASE),
      createSocket,
      readToken: readAccessToken,
      refreshToken: freshToken,
      onFrame: (frame: RealtimeFrame): void => {
        if (frame.type === "conversation.typing") {
          // The presence store, not the cache: a typing ping is live state, not data.
          if (frame.conversation_id !== undefined && frame.user_id !== undefined) {
            usePresence.getState().markTyping(Number(frame.conversation_id), {
              userId: Number(frame.user_id),
              name: null,
              seenAt: Date.now(),
            });
          }
          return;
        }
        invalidateAll(queryClient, keysForFrame(frame));
      },
      onPollTick: (): void => {
        invalidateAll(queryClient, ALL_INBOX_KEYS);
      },
      onStatus: publishStatus,
    });

    client.start();
    return (): void => {
      client.stop();
    };
  }, [queryClient, hasAgency]);
}
